/* Phase 2A-2B — Client website research panel.
 * Founder/beta-user friendly:
 *  - Shows current research status (not analyzed / analyzed / failed / stale).
 *  - One-click "Analyze website" (uses client.website by default; allows
 *    overriding the URL).
 *  - "Refresh research" forces a re-fetch (bypasses 30-day cache).
 *  - Shows extracted facts (title, meta, headings, offers, CTAs, tone,
 *    audience, claims) with the source URL as a clickable citation.
 *  - Shows source labels: website_verified vs ai_inference.
 *
 * Backend contract:
 *  - POST /api/clients/{id}/research/website {url, force_refresh, source_name}
 *  - GET  /api/clients/{id}/research
 */
import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
    RefreshCw, Globe, CheckCircle2, AlertCircle, Loader2,
    ChevronDown, ChevronUp, Link as LinkIcon, X as XIcon,
} from "lucide-react";

const STALE_AFTER_DAYS = 30;

function classifyRow(row) {
    if (!row) return { label: "not analyzed", tone: "neutral" };
    if (row.status === "failed") return { label: "failed", tone: "error" };
    if (row.status === "skipped") return { label: "skipped", tone: "neutral" };
    const exp = row.expires_at ? new Date(row.expires_at) : null;
    if (exp && exp < new Date()) return { label: "stale", tone: "warn" };
    return { label: "verified", tone: "ok" };
}

function StatusPill({ row }) {
    const { label, tone } = classifyRow(row);
    const toneClass = {
        ok: "border-emerald-500/30 text-emerald-300 bg-emerald-500/5",
        warn: "border-amber-500/30 text-amber-300 bg-amber-500/5",
        error: "border-red-500/40 text-red-300 bg-red-500/5",
        neutral: "border-zinc-700 text-zinc-400 bg-zinc-900/40",
    }[tone];
    return (
        <span
            data-testid="research-status-pill"
            className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-sm border ${toneClass}`}
        >
            {label}
        </span>
    );
}

export default function ResearchPanel({ clientId, defaultUrl }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [analyzing, setAnalyzing] = useState(false);
    const [urlInput, setUrlInput] = useState(defaultUrl || "");
    const [openIds, setOpenIds] = useState(new Set());

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/clients/${clientId}/research`);
            setRows(data.research || []);
        } catch (e) {
            toast.error(e.userMessage || "Could not load research");
        } finally { setLoading(false); }
    }, [clientId]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { setUrlInput(defaultUrl || ""); }, [defaultUrl]);

    const runAnalysis = async (forceRefresh = false) => {
        const url = (urlInput || defaultUrl || "").trim();
        if (!url) {
            toast.error("Enter a website URL to analyze.");
            return;
        }
        setAnalyzing(true);
        try {
            const { data } = await api.post(
                `/clients/${clientId}/research/website`,
                { url, force_refresh: forceRefresh }
            );
            if (data.status === "success") {
                if (data.cached) toast.success("Loaded cached research.");
                else toast.success("Website analyzed — facts saved.");
            } else {
                toast.error(`Could not analyze: ${data.error_summary || data.error_kind}`);
            }
            await load();
        } catch (e) {
            toast.error(e.userMessage || "Research request failed");
        } finally { setAnalyzing(false); }
    };

    const toggleOpen = (id) => {
        const n = new Set(openIds);
        if (n.has(id)) n.delete(id); else n.add(id);
        setOpenIds(n);
    };

    const websiteRows = rows.filter(r => r.source_type === "client_website");
    const primary = websiteRows[0] || null;

    return (
        <div className="card-base p-5" data-testid="client-research-panel">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                    <Globe className="w-4 h-4 text-zinc-500" />
                    <h3 className="font-display text-lg">Website research</h3>
                    {primary && <StatusPill row={primary} />}
                </div>
                <div className="flex gap-2">
                    {primary && (
                        <button
                            type="button"
                            onClick={() => runAnalysis(true)}
                            disabled={analyzing}
                            data-testid="research-refresh-btn"
                            className="btn-secondary text-xs disabled:opacity-50"
                            title="Force re-fetch (bypass 30-day cache)"
                        >
                            <RefreshCw className={`w-3.5 h-3.5 ${analyzing ? "animate-spin" : ""}`} />
                            Refresh
                        </button>
                    )}
                </div>
            </div>

            <div className="flex gap-2 mb-4">
                <input
                    type="url"
                    value={urlInput}
                    onChange={(e) => setUrlInput(e.target.value)}
                    placeholder="https://your-client.com"
                    className="input-base flex-1 text-sm"
                    data-testid="research-url-input"
                />
                <button
                    type="button"
                    onClick={() => runAnalysis(false)}
                    disabled={analyzing || !urlInput.trim()}
                    data-testid="research-analyze-btn"
                    className="btn-primary text-xs disabled:opacity-50"
                >
                    {analyzing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Analyze website"}
                </button>
            </div>

            <p className="text-xs text-zinc-500 mb-4 leading-relaxed">
                Furmais fetches only the homepage (≤2&nbsp;MB, 10&nbsp;s timeout, redirects capped at 3).
                Extracted facts are cached for 30 days and used by Brand DNA, Research, and
                Competitor Analyst with explicit source attribution. Loading 200&times; faster than
                re-asking the model to invent.
            </p>

            {loading && <div className="text-xs text-zinc-500">Loading…</div>}
            {!loading && websiteRows.length === 0 && (
                <div className="text-xs text-zinc-500 italic">
                    Not analyzed yet. Click <span className="text-zinc-300">Analyze website</span> above.
                </div>
            )}

            {primary && (
                <DiscoveredSocialLinksPanel
                    clientId={clientId}
                    primaryResearchId={primary.id}
                />
            )}

            <div className="space-y-3">
                {websiteRows.map(r => (
                    <ResearchRow
                        key={r.id}
                        row={r}
                        open={openIds.has(r.id)}
                        onToggle={() => toggleOpen(r.id)}
                    />
                ))}
            </div>
        </div>
    );
}

function ResearchRow({ row, open, onToggle }) {
    const facts = row.extracted_facts || {};
    const failed = row.status !== "success";
    return (
        <div
            className={`border rounded-sm ${failed ? "border-red-500/30 bg-red-500/5" : "border-zinc-800 bg-zinc-950/50"}`}
            data-testid={`research-row-${row.id}`}
        >
            <button
                type="button"
                onClick={onToggle}
                className="w-full flex items-center justify-between gap-3 px-3 py-2 text-left hover:bg-zinc-900/40"
            >
                <div className="flex items-center gap-2 min-w-0">
                    {failed
                        ? <AlertCircle className="w-3.5 h-3.5 text-red-400 shrink-0" />
                        : <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                    }
                    <span className="text-sm text-zinc-200 truncate">
                        {facts.title || row.source_url || "—"}
                    </span>
                    <span
                        className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm border border-zinc-800 text-zinc-500 shrink-0"
                        data-testid="research-source-label"
                        title="Source attribution"
                    >
                        {failed ? row.error_kind || "failed" : "website_verified"}
                    </span>
                </div>
                {open ? <ChevronUp className="w-3.5 h-3.5 text-zinc-500" /> : <ChevronDown className="w-3.5 h-3.5 text-zinc-500" />}
            </button>
            {open && (
                <div className="px-3 pb-3 space-y-2 text-xs">
                    <Field label="Source URL">
                        <a href={row.source_url} target="_blank" rel="noreferrer" className="text-zinc-300 hover:underline break-all">
                            {row.source_url}
                        </a>
                    </Field>
                    {failed ? (
                        <Field label="Error">{row.error_summary || row.error_kind}</Field>
                    ) : (
                        <>
                            <Field label="Title">{facts.title || "—"}</Field>
                            <Field label="Meta description">{facts.meta_description || "—"}</Field>
                            {facts.headings?.length > 0 && (
                                <Field label="Headings">{facts.headings.join(" | ")}</Field>
                            )}
                            {facts.offers?.length > 0 && (
                                <Field label="Offers / services">{facts.offers.join(", ")}</Field>
                            )}
                            {facts.cta_phrases?.length > 0 && (
                                <Field label="CTAs">{facts.cta_phrases.join(" · ")}</Field>
                            )}
                            {facts.tone_signals?.length > 0 && (
                                <Field label="Tone signals">{facts.tone_signals.join(" / ")}</Field>
                            )}
                            {facts.audience_clues?.length > 0 && (
                                <Field label="Audience clues">{facts.audience_clues.join(", ")}</Field>
                            )}
                            {facts.claims?.length > 0 && (
                                <Field label="Claims found on page">
                                    <ul className="list-disc pl-4 space-y-0.5">
                                        {facts.claims.map((c, i) => <li key={i}>{c}</li>)}
                                    </ul>
                                </Field>
                            )}
                            <div className="text-[10px] text-zinc-600 pt-1">
                                Fetched {new Date(row.fetched_at).toLocaleString()} · expires {new Date(row.expires_at).toLocaleDateString()}
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <div className="label-micro mb-1">{label}</div>
            <div className="text-zinc-200 whitespace-pre-wrap break-words">{children}</div>
        </div>
    );
}

// ============================================================
// iter67/R1 — Discovered social links Confirm-and-Fill panel.
//
// Renders the structured social_links produced by
// website_research._extract_social_links() for the latest
// successful website research row. Per-link Approve / Reject
// buttons; conflict detection surfaces a Keep / Replace choice
// before any client field is overwritten.
// ============================================================
const _PLATFORM_LABELS = {
    instagram: "Instagram",
    facebook:  "Facebook",
    linkedin:  "LinkedIn",
    youtube:   "YouTube",
    x:         "X / Twitter",
    tiktok:    "TikTok",
    pinterest: "Pinterest",
    behance:   "Behance",
    dribbble:  "Dribbble",
    whatsapp:  "WhatsApp",
    google_business_profile: "Google Business",
    other:     "Other",
};

function DiscoveredSocialLinksPanel({ clientId, primaryResearchId }) {
    const [discovered, setDiscovered] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
    const [pendingId, setPendingId] = useState("");
    // Map<linkKey, "approved"|"rejected"|"kept_existing">
    const [resolved, setResolved] = useState({});
    // conflict dialog state
    const [conflict, setConflict] = useState(null);

    const linkKey = (e) => `${e.platform}::${e.url}`;

    const load = useCallback(async () => {
        if (!clientId) return;
        setLoading(true);
        try {
            const { data } = await api.get(
                `/clients/${clientId}/discovered-social-links`,
            );
            setDiscovered(data.discovered || []);
            setHasLoadedOnce(true);
        } catch (e) {
            // Quiet — surface only if the user explicitly retried.
        } finally {
            setLoading(false);
        }
    }, [clientId]);

    useEffect(() => { load(); }, [load, primaryResearchId]);

    const post = async ({ platform, url, action, replace,
                          source_url, source_location }) => {
        return api.post(`/clients/${clientId}/social-links/confirm`, {
            platform, url, action, replace,
            source_url, source_location,
        });
    };

    const onApprove = async (entry, opts = {}) => {
        const k = linkKey(entry);
        setPendingId(k);
        try {
            await post({
                platform: entry.platform,
                url: entry.url,
                action: "approve",
                replace: !!opts.replace,
                source_url: entry.source_url,
                source_location: entry.source_location,
            });
            setResolved(prev => ({ ...prev, [k]: "approved" }));
            toast.success(
                `${_PLATFORM_LABELS[entry.platform] || entry.platform} `
                + `attached to client.`,
            );
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (detail && detail.error === "social_link_conflict") {
                setConflict({ entry, detail });
                return;   // keep pendingId until the user resolves
            }
            toast.error(detail || "Could not approve link.");
        } finally {
            if (!conflict) setPendingId("");
        }
    };

    const onReject = async (entry) => {
        const k = linkKey(entry);
        setPendingId(k);
        try {
            await post({
                platform: entry.platform,
                url: entry.url,
                action: "reject",
                replace: false,
                source_url: entry.source_url,
                source_location: entry.source_location,
            });
            setResolved(prev => ({ ...prev, [k]: "rejected" }));
            toast.success(`Rejected (won't be attached).`);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            toast.error(detail || "Could not reject link.");
        } finally {
            setPendingId("");
        }
    };

    const resolveConflict = async (mode) => {
        if (!conflict) return;
        const { entry } = conflict;
        if (mode === "replace") {
            await onApprove(entry, { replace: true });
        } else {
            const k = linkKey(entry);
            setResolved(prev => ({ ...prev, [k]: "kept_existing" }));
            toast.message("Existing value kept. Discovered link not used.");
        }
        setConflict(null);
        setPendingId("");
    };

    if (!discovered.length && !loading) {
        // iter67/R1.1 — Codex YELLOW: when website analysis succeeded
        // but no clickable social anchors exist in the DOM (e.g.
        // piyushpooja.teachable.com mentions "PIYUSHPOOJATV" only as
        // text), surface a truthful empty state. NEVER invent profile
        // URLs from text mentions.
        if (!hasLoadedOnce) return null;
        return (
            <div
                className="border border-zinc-800 rounded-sm bg-zinc-950/60 p-4 mb-4"
                data-testid="discovered-social-links-empty"
            >
                <div className="flex items-center gap-2 mb-1">
                    <LinkIcon className="w-4 h-4 text-zinc-500" />
                    <h4 className="font-display text-sm text-zinc-300">
                        Found social links
                    </h4>
                    <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
                        0 discovered
                    </span>
                </div>
                <p className="text-[11px] text-zinc-500 leading-relaxed">
                    No public social profile links found on this
                    analyzed page. If the page mentions a social
                    handle only in text (not as a clickable link),
                    Furmais does NOT invent a URL — add it manually
                    in the client form instead.
                </p>
            </div>
        );
    }

    return (
        <div
            className="border border-zinc-800 rounded-sm bg-zinc-950/60 p-4 mb-4"
            data-testid="discovered-social-links-panel"
        >
            <div className="flex items-center gap-2 mb-3">
                <LinkIcon className="w-4 h-4 text-emerald-400" />
                <h4 className="font-display text-sm text-zinc-200">
                    Found social links
                </h4>
                <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
                    {discovered.length} discovered
                </span>
            </div>
            <p className="text-[11px] text-zinc-500 mb-3 leading-relaxed">
                Furmais found these social profile links in the public
                anchors of the analyzed website. Approve the ones you want
                attached to this client. Existing client values are never
                overwritten without your explicit choice.
            </p>

            <div className="space-y-2">
                {discovered.map((entry) => {
                    const k = linkKey(entry);
                    const decision = resolved[k];
                    const isPending = pendingId === k;
                    return (
                        <div
                            key={k}
                            data-testid={`discovered-social-row-${entry.platform}`}
                            className="flex items-center gap-3 p-2 rounded-sm border border-zinc-800 bg-zinc-900/40"
                        >
                            <span
                                className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm border border-zinc-700 text-zinc-400 shrink-0 w-24 text-center"
                                title={entry.platform}
                            >
                                {_PLATFORM_LABELS[entry.platform] || entry.platform}
                            </span>
                            <a
                                href={entry.url}
                                target="_blank"
                                rel="noreferrer"
                                className="flex-1 text-xs text-zinc-200 hover:underline truncate"
                                title={entry.url}
                            >
                                {entry.url}
                            </a>
                            <span
                                className="text-[10px] font-mono text-zinc-500 shrink-0"
                                title="Where on the website this anchor was found"
                            >
                                {entry.source_location}
                            </span>
                            {entry.conflict && !decision && (
                                <span
                                    data-testid={`discovered-social-conflict-${entry.platform}`}
                                    className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm border border-amber-500/40 text-amber-300 bg-amber-500/10 shrink-0"
                                    title={`Existing value: ${entry.existing_value}`}
                                >
                                    conflict
                                </span>
                            )}

                            {decision ? (
                                <span
                                    data-testid={`discovered-social-status-${entry.platform}`}
                                    className={`text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm border shrink-0 ${
                                        decision === "approved"
                                            ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10"
                                            : decision === "kept_existing"
                                            ? "border-zinc-700 text-zinc-400 bg-zinc-900"
                                            : "border-red-500/40 text-red-300 bg-red-500/5"
                                    }`}
                                >
                                    {decision === "approved" ? "approved"
                                        : decision === "kept_existing" ? "kept existing"
                                        : "rejected"}
                                </span>
                            ) : (
                                <div className="flex gap-1 shrink-0">
                                    <button
                                        type="button"
                                        onClick={() => onApprove(entry)}
                                        disabled={isPending}
                                        data-testid={`approve-social-${entry.platform}`}
                                        className="btn-primary text-[10px] px-2 py-1 disabled:opacity-50"
                                    >
                                        {isPending && !conflict
                                            ? <Loader2 className="w-3 h-3 animate-spin" />
                                            : <CheckCircle2 className="w-3 h-3" />}
                                        Approve
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => onReject(entry)}
                                        disabled={isPending}
                                        data-testid={`reject-social-${entry.platform}`}
                                        className="btn-secondary text-[10px] px-2 py-1 disabled:opacity-50"
                                    >
                                        <XIcon className="w-3 h-3" />
                                        Reject
                                    </button>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {conflict && (
                <div
                    data-testid="social-conflict-modal"
                    className="mt-3 p-3 border border-amber-500/40 bg-amber-500/10 rounded-sm"
                >
                    <div className="text-xs text-amber-200 font-display mb-2 flex items-center gap-2">
                        <AlertCircle className="w-3.5 h-3.5" />
                        Existing {_PLATFORM_LABELS[conflict.entry.platform]} value differs
                    </div>
                    <div className="text-[11px] text-zinc-300 mb-2 space-y-1">
                        <div>
                            <span className="text-zinc-500">Existing:</span>{" "}
                            <span className="font-mono">{conflict.detail.existing_value}</span>
                        </div>
                        <div>
                            <span className="text-zinc-500">Discovered:</span>{" "}
                            <span className="font-mono">{conflict.detail.discovered_value}</span>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            data-testid="social-conflict-replace-btn"
                            onClick={() => resolveConflict("replace")}
                            className="btn-primary text-[10px] px-2 py-1"
                        >
                            Replace with discovered
                        </button>
                        <button
                            type="button"
                            data-testid="social-conflict-keep-btn"
                            onClick={() => resolveConflict("keep")}
                            className="btn-secondary text-[10px] px-2 py-1"
                        >
                            Keep existing
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
